import {PlusIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, ButtonGroup, useRefObjectAsForwardedRef} from '@primer/react'
import type {RefObject, MouseEvent} from 'react'
import {useCallback, useRef} from 'react'

import {useActionBarDialogs} from '../../hooks/use-action-bar-dialogs'
import {useStartConversationDialog} from '../../hooks/use-start-conversation-dialog'
import {useActionBarFocus} from '../../hooks/use-action-bar-focus'
import type {ClientDiffLine, FileDiffReference} from '../../types'
import CommentIndicator from './CommentIndicator'
import {CellContextMenu} from './CellContextMenu'
import {InProgressCommentIndicator} from '../InProgressCommentIndicator'
import styles from './ActionBar.module.css'
import {clsx} from 'clsx'
import {CommentsPreference} from '@github-ui/diff-view-settings/types'
import type {DiffAnnotation, EnterDialogModeFunction, Thread, ViewerData} from '../../conversation-types'
import type {HunkData} from '../../helpers/hunk-data-helpers'
import type {DiffAnchor} from '@github-ui/diffs/types'
import {getGlobalINPObserver} from '@github-ui/web-vitals'
import {useAnalytics} from '@github-ui/use-analytics'
import {sendCustomMetric} from '@github-ui/stats'

/**
 * The ActionBar is a set of buttons that appears on hover or focus of a cell in the diff grid.
 * The buttons will be absolutely positioned to the right side of the diff cell.
 */
type ActionBarProps = {
  annotations: DiffAnnotation[]
  authorLogin: string
  authorAvatarUrl: string
  cellRef: RefObject<HTMLTableCellElement | null>
  cellId?: string
  copilotChatReference?: FileDiffReference
  currentHunk: HunkData | undefined
  diffAnchor: DiffAnchor
  diffLine: ClientDiffLine
  enterDialogMode?: EnterDialogModeFunction
  handleCopyCode?: () => void
  hasDraftComment?: boolean
  isLeftSide: boolean
  isSplit: boolean
  nextHunk: HunkData | undefined
  onMouseDownAddCommentButton: (e: MouseEvent) => void
  onMouseLeaveAddCommentButton: (e: MouseEvent) => void
  previousHunk: HunkData | undefined
  shouldDisplayCollapseComments?: boolean
  shouldDisplayMinimizedComments?: boolean
  showStartConversation: boolean
  threads: Thread[]
  toggleViewingMarkers: () => void
  viewerData: ViewerData
  ref?: React.Ref<HTMLElement>
}

export function ActionBar({
  ref: dialogReturnFocusRef = null,
  annotations,
  authorAvatarUrl,
  authorLogin,
  cellId,
  cellRef,
  copilotChatReference,
  currentHunk,
  diffAnchor,
  diffLine,
  enterDialogMode,
  hasDraftComment,
  handleCopyCode,
  isLeftSide,
  isSplit,
  nextHunk,
  onMouseDownAddCommentButton,
  onMouseLeaveAddCommentButton,
  previousHunk,
  shouldDisplayCollapseComments,
  shouldDisplayMinimizedComments,
  showStartConversation,
  threads,
  toggleViewingMarkers,
  viewerData,
}: ActionBarProps) {
  const viewConversationButtonRef = useRef<HTMLButtonElement>(null)
  const startConversationButtonRef = useRef<HTMLButtonElement>(null)
  const {lineSpacingPreference, commentsPreference} = viewerData

  const contextMenuRef = useRef<HTMLButtonElement>(null)
  const {sendAnalyticsEvent} = useAnalytics()

  // When there are no markers, return focus back to start conversation button

  // eslint-disable-next-line react-hooks/refs
  const returnFocusToRef = viewConversationButtonRef.current ? viewConversationButtonRef : startConversationButtonRef

  useRefObjectAsForwardedRef(dialogReturnFocusRef, returnFocusToRef)

  const totalCommentsCount = threads.reduce((acc, thread) => acc + (thread.commentsData?.comments.length ?? 0), 0)
  const totalAnnotationsCount = annotations.length
  const totalCommentsAndAnnotationsCount = totalCommentsCount + totalAnnotationsCount

  const hasThreads = totalCommentsAndAnnotationsCount > 0

  const {isActionBarFocused, handleActionBarBlur, handleActionBarFocusCapture, handleActionBarKeydownCapture} =
    useActionBarFocus({cellRef})

  const {isContextMenuOpen, anyMenuOpen, toggleContextMenu, toggleContextMenuFromActionBar} = useActionBarDialogs({
    cellId,
    actionBarRef: contextMenuRef,
  })
  const {startNewConversation, startNewConversationWithSuggestedChange} = useStartConversationDialog({
    cellId,
    onOpenDialog: enterDialogMode,
  })

  const startConversationOnClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      getGlobalINPObserver()?.registerCallback({
        event: e.nativeEvent,
        cb: interaction => {
          sendAnalyticsEvent('diff-lines.inp_interaction', 'CELL_ADD_COMMENT_BUTTON', {
            latency: interaction.latency,
          })
          sendCustomMetric({
            name: 'PRS_COMMENT_BOX_INP',
            value: interaction.latency,
          })
        },
      })

      e.stopPropagation()
      startNewConversation()
    },
    [startNewConversation, sendAnalyticsEvent],
  )

  const viewConversations = hasThreads && commentsPreference === CommentsPreference.Collapsed

  const sharedProps = {
    onBlur: handleActionBarBlur,
    onFocusCapture: handleActionBarFocusCapture,
    onKeyDownCapture: handleActionBarKeydownCapture,
  }

  const handleOnClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      // if there's a single thread or annotation, prevent default so we don't select the cell.
      // we'll let the conversation selection logic handle selecting the cell(s) associated to the comment.
      if (threads.length === 1 || annotations.length === 1) {
        event.preventDefault()
      }

      toggleViewingMarkers?.()
    },
    [annotations.length, threads.length, toggleViewingMarkers],
  )

  return (
    <>
      {showStartConversation && (
        <div aria-hidden={!isActionBarFocused} className={clsx('d-flex', 'flex-row', styles.addCommentButton)}>
          <>
            {hasDraftComment ? (
              <Button
                aria-label="Continue comment in progress"
                size="small"
                className={clsx(
                  'py-0',
                  lineSpacingPreference === 'compact'
                    ? styles.addCommentButtonDraftCompact
                    : styles.addCommentButtonDraftRelaxed,
                  styles.actionBarHeight,
                )}
                onClick={startNewConversation}
                {...sharedProps}
              >
                <InProgressCommentIndicator
                  lineSpacingPreference={lineSpacingPreference}
                  authorAvatarUrl={authorAvatarUrl}
                  authorLogin={authorLogin}
                />
              </Button>
            ) : (
              <Button
                icon={PlusIcon}
                aria-label="Add comment"
                ref={startConversationButtonRef}
                size="small"
                data-add-comment-button
                className={clsx(
                  'fgColor-muted',
                  'bgColor-accent-emphasis',
                  'borderColor-accent-emphasis',
                  'fgColor-onEmphasis',
                  'px-0',
                  styles.addCommentButtonNew,
                  styles.actionBarHeight,
                  styles.actionBarStartCommentWidth,
                )}
                onClick={startConversationOnClick}
                onMouseDown={onMouseDownAddCommentButton}
                onMouseLeave={onMouseLeaveAddCommentButton}
                {...sharedProps}
              />
            )}
          </>
        </div>
      )}
      <div aria-hidden={!isActionBarFocused} className={clsx('d-flex', 'flex-row', styles['action-bar-position'])}>
        <ButtonGroup className={styles['action-bar-button-group']} {...sharedProps}>
          {viewConversations && shouldDisplayMinimizedComments && (
            <Button
              ref={viewConversationButtonRef}
              aria-expanded={anyMenuOpen}
              aria-label="View comments"
              size="small"
              className={clsx('py-0', 'px-1', styles.actionBarHeight)}
              onClick={handleOnClick}
            >
              <CommentIndicator
                threads={threads}
                annotations={annotations}
                lineSpacingPreference={lineSpacingPreference}
              />
            </Button>
          )}

          <ActionMenu anchorRef={contextMenuRef} open={isContextMenuOpen} onOpenChange={toggleContextMenu}>
            <ActionMenu.Anchor>
              <Button
                ref={contextMenuRef}
                aria-haspopup="true"
                aria-label="More actions"
                icon={TriangleDownIcon}
                size="small"
                className={clsx('fgColor-muted', styles.actionBarArrowSizing)}
                onClick={toggleContextMenuFromActionBar}
              />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay width="medium">
              <ActionList>
                <CellContextMenu
                  threads={threads}
                  annotations={annotations}
                  currentHunk={currentHunk}
                  diffAnchor={diffAnchor}
                  diffLine={diffLine}
                  shouldDisplayCollapseComments={shouldDisplayCollapseComments}
                  copilotChatReference={copilotChatReference}
                  showStartConversation={showStartConversation}
                  handleCopyCode={handleCopyCode}
                  handleViewMarkersSelection={toggleViewingMarkers}
                  isLeftSide={isLeftSide}
                  isSplit={isSplit}
                  nextHunk={nextHunk}
                  previousHunk={previousHunk}
                  startConversationCurrentLine={startNewConversation}
                  startConversationWithSuggestedChange={startNewConversationWithSuggestedChange}
                  viewerData={viewerData}
                />
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        </ButtonGroup>
      </div>
    </>
  )
}

ActionBar.displayName = 'ActionBar'
